import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';

import {catchError} from "rxjs/operators";
import {ConfigurationService} from "./configuration.service";
import {HttpHeaders} from '@angular/common/http';
import {ErrorService} from "./error.service";
import {MessageService} from "./message.service";
import {Invoice} from "../shared/model/Invoice";
import {InvoiceStore} from "../shared/model/InvoiceStore";
import {SelectAllCheckboxState} from "@progress/kendo-angular-grid";
import {BaseService} from "~/app/core/services/base.service";


@Injectable({
  providedIn: 'root'
})
export class InvoicesService {
  readonly INVOICESURL = '/invoice/invoices';
  readonly STORESURL = '/invoice/stores';
  readonly INVOICESUMMARYBYINVOICE = '/invoice/invoicesummaryinvoice';
  readonly INVOICESUMMARYBYDATE = '/invoice/invoicesummarydate';
  readonly INVOICEREPORTURL = '/invoice/invoicereportview';
  readonly INVOICEDOWNLOADCSV = '/invoice/csvinvoice';
  readonly INVOICEDOWNLOADXML = '/invoice/searchxmlinvoice';
  readonly XMLINVOICE = '/invoice/xmlinvoice';
  readonly INVOICEDOWNLOADFORMAT = '/invoice/csvformatted';

  response: string;
  selectAllState: SelectAllCheckboxState = 'unchecked';
  selectAllChecked: boolean = false;
  masterStore: boolean = false;
  masterStoreInvoice: boolean = false;

  constructor(private baseService: BaseService,
              private configuration: ConfigurationService,
              private errorService: ErrorService,
              private messageService: MessageService) {
  }

  setSelectAllState(selectedObj: SelectAllCheckboxState) {
    this.selectAllState = selectedObj;
  }

  getSelectAllState(): SelectAllCheckboxState {
    return this.selectAllState;
  }

  setMasterStoreSelect(selected: boolean) {
    this.masterStore = selected;
  }

  getMasterStoreSelect(): boolean {
    return this.masterStore;
  }

  setMasterInvoiceSelect(selected: boolean) {
    this.masterStoreInvoice = selected;
  }

  getMasterInvoiceSelect(): boolean {
    return this.masterStoreInvoice;
  }

  getInvoicesSummary(parents, stores, requestType, downloaded, fromRange, toRange, fromDate, toDate): Observable<Invoice[]> {
    var varFrom = 0;
    if (fromRange) {
      varFrom = fromRange;
    }

    var varTo = 0;
    if (toRange) {
      varTo = toRange;
    }

    const data = {
      'parents': parents.toString(),
      'stores': stores.toString(),
      'requestType': requestType,
      'invStart': fromRange,
      'invEnd': toRange,
      'onlyNew': downloaded
    }
    return this.baseService.postHttpJsonObject<Invoice[]>(
      'getInvoicesSummary',
      'invoices-service',
      this.configuration.RESTURL + this.INVOICESUMMARYBYINVOICE, JSON.stringify(data))
      .pipe(
        catchError(this.errorService.handleError('InvoiceService getInvoicesSummary', []))
      );
  }

  getInvoiceSummaryDate(parents, stores, dateType, downloaded, fromDate, toDate): Observable<Invoice[]> {
    const data = {
      'parents': parents.toString(),
      'stores': stores.toString(),
      'dateType': dateType,
      'startDate': fromDate,
      'endDate': toDate,
      'onlyNew': downloaded
    }
    return this.baseService.postHttpJsonObject<Invoice[]>(
      'getInvoiceSummaryDate',
      'invoices-service',
      this.configuration.RESTURL + this.INVOICESUMMARYBYDATE, JSON.stringify(data))
      .pipe(
        catchError(this.errorService.handleError('InvoceService getInvoiceSummaryByDate', []))
      );
  }

  getStores(parents): Observable<InvoiceStore[]> {
    return this.baseService.getHttpObject<InvoiceStore[]>(
      'getStores',
      'invoices-service',
      this.configuration.RESTURL + this.STORESURL + "/" + parents)
      .pipe(
        //tap(Parent => this.log('Parents fetched')),
        catchError(this.errorService.handleError('StatementsService getParents', []))
      );
  }

  getInvoiceSummary(stores, reqType, downloaded) {
    return this.baseService.getHttpObject<Invoice[]>(
      'getInvoiceSummary',
      'invoices-service',
      this.configuration.RESTURL + this.INVOICESURL + "/" + stores + "/" + reqType + "/" + downloaded)
      .pipe(
        catchError(this.errorService.handleError('InvoicesService getStores', []))
      )
  }

  getInvoices(invoices) {
    const data = {
      'invoices': invoices.toString(),
      'type' : 'JDE'
    }
    return this.baseService.postHttpResponseText(
      'getInvoiceSummary',
      'invoices-service',
      this.configuration.RESTURL + this.INVOICEREPORTURL, JSON.stringify(data))
      .pipe(
        catchError(this.errorService.handleError('InvoiceService getInvoices', []))
      )
  }

  downloadCSV(invoices) {
    const headers = new HttpHeaders(this.headers());
    const body = JSON.stringify(invoices);
    return this.baseService.postHttpJsonBlobOptions(
      'downloadCSV',
      'invoices-service',
      this.configuration.RESTURL + this.INVOICEDOWNLOADCSV,
      body,
      {
        headers: headers,
        responseType: 'blob'
      });
  }

  downloadCSVFormatted(invoices) {
    const headers = new HttpHeaders(this.headers());
    const body = JSON.stringify(invoices);
    return this.baseService.postHttpJsonBlobOptions(
      'downloadCSVFormatted',
      'invoices-service',
      this.configuration.RESTURL + this.INVOICEDOWNLOADFORMAT,
      body,
      {
        headers: headers,
        responseType: 'blob'
      });
  }

  downloadXML(invoices) {
    const headers = new HttpHeaders(this.headers());
    const body = JSON.stringify(invoices);
    return this.baseService.postHttpJsonBlobOptions(
      'downloadXML',
      'invoices-service',
      this.configuration.RESTURL + this.INVOICEDOWNLOADXML, body, {
        headers: headers,
        responseType: 'blob'
      });
  }

  getXmlInvoice() {
    return this.baseService.getHttp(
      'getXmlInvoice',
      'invoices-service',
      this.configuration.RESTURL + this.XMLINVOICE);
  }

  getXML() {
    const headers = new HttpHeaders(this.headers());
    return this.baseService.getHttp(
      'getXML',
      'invoices-service',
      this.configuration.RESTURL + this.INVOICEDOWNLOADXML);
  }

  getTextFileName1(filename: string) {
    return this.baseService.getHttpOptions(
      'getTextFileName1',
      'invoices-service',
      filename, {responseType: 'text'})
  }

  xmlStringToJson(xml: string): string {
    const oParser = new DOMParser();
    const xmlDoc = oParser.parseFromString(xml, 'text/xml');
    const doc = xmlDoc.getElementsByTagName("URL")[0].innerHTML;
    return doc;
  }

  submitReport() {
    const headers = new HttpHeaders(this.headers());
    const options: any = {headers, withCredentials: true};
    const body = JSON.stringify('');
    return this.baseService.postHttpJson(
      'submitReport',
      'invoices-service',
      'google.com', body, options);
  }

  headers(): {} {
    return {
      'Accept': 'application/json',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      'Content-Type': 'application/json',
      'Expires': 'Sat, 01 Jan 2000 00:00:00 GMT'
    };
  }


  /** Log a service message with the MessageService */
  private log(message: string) {
    this.messageService.add(`CreateGroupService: ${message}`);
  }
}
